import React, { useEffect, useState } from "react"
import * as layouts from "layouts"
import * as errors from "errors"
import * as brands from "brands"
import * as authzc from "authzcached"
import * as api from "brandguard/api"
import * as httpx from "httpx"
import * as training from "brandguard/display/training"
import * as icons from "icons"
import * as inputs from "inputs"

interface props extends layouts.containers.FlexProps {
	onReactivate?(): void
	refreshTrigger?: number
}

export default function Kernels(props: React.PropsWithChildren<props>): JSX.Element {
	const { onReactivate, refreshTrigger, ...rest } = props
	const brand = brands.caching.useCached()
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const [cause, setCause] = useState(undefined as errors.Cause)
	const [loading, setLoading] = useState(false)
	const [req, setReq] = useState(api.documents.kernel.searches.request({ brand_id: brand.id, limit: 10n }))
	const [kernels, setKernels] = useState(api.documents.kernel.searches.response())
	const [activeKernel, setActiveKernel] = useState<api.documents.kernel.KernelMetadata | null>(null)
	const [editingVersion, setEditingVersion] = useState(null as string | null)
	const [editedName, setEditedName] = useState("")

	useEffect(() => {
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.documents.kernel.search(brand.id, req, authzc.bearer(authzaccount)))
			.then((kernels) => {
				setKernels(kernels)
				return api.documents.kernel.getactive(brand.id, brand.id, authzc.bearer(authzaccount))
			})
			.then((activeKernel) => {
				setActiveKernel(activeKernel)
				setLoading(false)
			})
			.catch((cause) => {
				setLoading(false)
				setCause(
					<errors.Textual cause={cause} logging={console.error}>
						Unable to load kernels
					</errors.Textual>,
				)
			})

		return p.cancel
	}, [req, refreshTrigger])

	const handleSaveName = (id: string, version: string) => {
		setLoading(true)
		const retry = httpx.autoretry()
		retry
			.wrap(() => api.documents.kernel.rename(brand.id, id, version, editedName, authzc.bearer(authzaccount)))
			.then(() => {
				setEditingVersion(null)
				setEditedName("")
				return api.documents.kernel.search(brand.id, req, authzc.bearer(authzaccount))
			})
			.then((updatedKernels) => {
				setKernels(updatedKernels)
			})
			.catch((cause) => {
				setCause(
					<errors.Textual cause={cause} logging={console.error}>
						Unable to rename kernel
					</errors.Textual>,
				)
			})
			.finally(() => setLoading(false))
	}

	const handleReactivate = (id: string, version: string) => {
		setLoading(true)
		const retry = httpx.autoretry()
		retry
			.wrap(() => api.documents.kernel.reactivate(brand.id, id, version, authzc.bearer(authzaccount)))
			.then(() => {
				return api.documents.kernel.search(brand.id, req, authzc.bearer(authzaccount))
			})
			.then((updatedKernels) => {
				setKernels(updatedKernels)
				return api.documents.kernel.getactive(brand.id, brand.id, authzc.bearer(authzaccount))
			})
			.then((activeKernel) => {
				setActiveKernel(activeKernel)
				onReactivate?.()
			})
			.catch((cause) => {
				setCause(
					<errors.Textual cause={cause} logging={console.error}>
						Unable to reactivate kernel
					</errors.Textual>,
				)
			})
			.finally(() => setLoading(false))
	}

	return (
		<layouts.Flex className="kernels" flexDirection="column" flex="1" {...rest}>
			<layouts.tables.container overflow="auto" flex="1">
				<errors.overlay styled cause={cause}>
					<layouts.loading.pending loading={loading}>
						<table style={{ color: layouts.theme.colors.grey.dark20 }}>
							<thead>
								<tr>
									<training.layouts.TableHead>Name</training.layouts.TableHead>
									<training.layouts.TableHead>Version</training.layouts.TableHead>
									<training.layouts.TableHead>Status</training.layouts.TableHead>
									<training.layouts.TableHead>Updated At</training.layouts.TableHead>
									<training.layouts.TableHead>Created At</training.layouts.TableHead>
									<training.layouts.TableHead>Actions</training.layouts.TableHead>
								</tr>
							</thead>
							<tbody>
								{kernels.items.map((c) => {
									const isActive = activeKernel && c.settings_md5 === activeKernel.settings_md5
									return (
										<React.Fragment key={c.version}>
											<training.layouts.TableRow>
												<training.layouts.TableData>
													{editingVersion === c.version ? (
														<layouts.Flex alignItems="center">
															<inputs.Text
																autoFocus
																key={c.version}
																defaultValue={editedName}
																onChange={(evt) => setEditedName(evt.target.value)}
																className={training.layouts.styledTextInput}
															/>
															<layouts.Flex
																ml="5px"
																onClick={() => handleSaveName(c.id, c.version)}
																style={{ cursor: "pointer" }}
															>
																<icons.Check width="14px" height="14px" fill={layouts.theme.colors.grey.dark20} />
															</layouts.Flex>
															<layouts.Flex
																ml="5px"
																onClick={() => {
																	setEditingVersion(null)
																	setEditedName("")
																}}
																style={{ cursor: "pointer" }}
															>
																<icons.Close width="14px" height="14px" fill={layouts.theme.colors.grey.dark20} />
															</layouts.Flex>
														</layouts.Flex>
													) : (
														<layouts.Flex alignItems="center">
															<layouts.Span>{c.name || "(unnamed)"}</layouts.Span>
															<layouts.Flex
																ml="5px"
																onClick={() => {
																	setEditingVersion(c.version)
																	setEditedName(c.name || "")
																}}
																style={{ cursor: "pointer" }}
															>
																<icons.Edit width="14px" height="14px" fill={layouts.theme.colors.grey.dark20} />
															</layouts.Flex>
														</layouts.Flex>
													)}
												</training.layouts.TableData>
												<training.layouts.TableData>{c.version}</training.layouts.TableData>
												<training.layouts.TableData>{api.Status[c.status]}</training.layouts.TableData>
												<training.layouts.TableData>{layouts.tables.Timestamp(c.updated_at)}</training.layouts.TableData>
												<training.layouts.TableData>{layouts.tables.Timestamp(c.created_at)}</training.layouts.TableData>
												<training.layouts.TableData>
													{isActive ? (
														<layouts.Span color={layouts.theme.colors.grey.dark50alpha50}>Active</layouts.Span>
													) : (
														<layouts.buttons.outline
															onClick={() => handleReactivate(c.id, c.version)}
															borderRadius="5px"
															px="10px"
															py="5px"
														>
															Reactivate
														</layouts.buttons.outline>
													)}
												</training.layouts.TableData>
											</training.layouts.TableRow>
										</React.Fragment>
									)
								})}
							</tbody>
						</table>
					</layouts.loading.pending>
				</errors.overlay>
			</layouts.tables.container>
			<layouts.pagination.Cursor
				m="auto"
				current={req.offset}
				advance={Number(kernels.next!.offset) === -1 ? undefined : kernels.next?.offset}
				onChange={(next) => {
					setReq({
						...req,
						offset: next,
					})
				}}
			/>
		</layouts.Flex>
	)
}
